import { Button } from 'components/atoms/Button';
import { FieldConnector } from 'components/atoms/Form/FieldConnector';
import FreeTextInput from 'components/atoms/Form/FreeTextInput';
import { Select } from 'components/atoms/Form/Select';
import OptionWithDelete from 'components/atoms/OptionWithDelete.component';
import PaymentAmount from 'components/molecules/Fields/PaymentAmount/PaymentAmount.component';
import CategoriesPicker from 'components/pages/Revenues/OtherRevenueForm/Fields/CategoriesPicker';
import useGetRevenuesVATRatesAPI from 'hooks/Revenues/useGetRevenuesVATRates';
import useRevenuesUnits from 'hooks/Revenues/useRevenuesUnits';
import { useEffect, type FC, type ReactNode } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InvoiceItem, InvoiceTypeEnum } from 'types/invoice.types';
import * as events from 'utils/amplitude/events/priceList.amplitude';
import { viewSuggestedChanges } from 'utils/applySuggestedChanges';
import { divideBy1000, multiplyBy1000, round } from 'utils/math';
import useFormMeta from './useFormMeta';

type FormProps = {
  formAPI: UseFormReturn;
  item: Partial<InvoiceItem>;
  onSubmit: (values: Partial<InvoiceItem>) => any;
  headerMeta?: ReactNode;
  footerMeta?: ReactNode;
  disabled?: boolean;
};

const Form: FC<FormProps> = ({
  headerMeta,
  footerMeta,
  onSubmit,
  formAPI,
  // item,
}) => {
  const { t } = useTranslation();

  const { options, defaultUnits, deleteUnitMutation } = useRevenuesUnits();

  const VATRatesAPI = useGetRevenuesVATRatesAPI();
  const VATRatesOptions = VATRatesAPI.rates.map((value: number) => ({
    label: `${(value * 100).toFixed()}%`,
    value,
  }));

  const { isSubmitting } = formAPI.formState;
  const formValues = formAPI.watch();

  const { getFormMeta, getSuggestedChanges } = useFormMeta();

  const meta = getFormMeta(formValues);

  useEffect(() => {
    viewSuggestedChanges(
      formValues,
      getSuggestedChanges(formValues),
      formAPI.setValue,
      true,
    );
  }, [formValues]);

  return (
    <FormProvider {...formAPI}>
      <form className="flex flex-col relative">
        <div className="flex flex-col px-6 pb-6">{headerMeta}</div>
        <div className="flex-1 flex flex-col gap-4 px-6">
          <FieldConnector name="name" label={t('clients.field.name')}>
            {({ field }) => {
              return (
                <FreeTextInput
                  {...field}
                  tracing={{ onFocus: events.WEBAPP_PRICEITEM_CLICKEDNAME }}
                  type="textarea"
                  placeholder={t('clients.field.name')}
                />
              );
            }}
          </FieldConnector>
          <FieldConnector
            name="description"
            label={`${t('invoice.form.description')} (${t(
              'invoice.field.optional.label',
            ).toLowerCase()})`}
          >
            {({ field }) => (
              <FreeTextInput
                placeholder={t('invoice.form.add_description')}
                type="textarea"
                {...field}
                tracing={{
                  onFocus: events.WEBAPP_PRICEITEM_CLICKEDDESCRIPTION,
                }}
                onChange={(e) => {
                  field.onChange(e.target.value);
                }}
              />
            )}
          </FieldConnector>
          <div className="flex gap-4">
            <FieldConnector
              className="flex-1"
              name="categoryId"
              label={t('expense.category')}
            >
              {({ field: { value, onChange, ...rest } }) => {
                return (
                  <CategoriesPicker
                    {...rest}
                    placeholder={t('invoice.field.select')}
                    value={value}
                    type={[
                      InvoiceTypeEnum.INVOICE,
                      InvoiceTypeEnum.OTHER_REVENUE,
                      InvoiceTypeEnum.CREDIT_NOTE,
                    ]}
                    tracing={{
                      onOpen: events.WEBAPP_PRICEITEM_CLICKEDCATEGORY,
                    }}
                    onChange={(category: any) => {
                      onChange(category.id);
                      formAPI.setValue('category', category);
                    }}
                  />
                );
              }}
            </FieldConnector>
            <FieldConnector
              className="flex-1"
              name="unit"
              label={t('invoice.form.unit')}
            >
              {({ field }) => (
                <Select
                  {...field}
                  tracing={{ onFocus: events.WEBAPP_PRICEITEM_CLICKEDUNIT }}
                  options={options}
                  creatable
                  isClearable
                  controlShouldRenderValue={
                    defaultUnits
                      .map(({ value }) => value)
                      .includes(field.value) || undefined
                  }
                  onChange={(option: any) => {
                    if (!option) return field.onChange(null);
                    field.onChange(option.value);
                  }}
                  formatOptionLabel={(option, meta) => {
                    if (meta.context === 'value') return option.label;
                    if (option.isDefault) return option.label;
                    return (
                      <OptionWithDelete
                        deleteButtonProps={{
                          disabled: deleteUnitMutation.isLoading,
                        }}
                        onDelete={() => {
                          return deleteUnitMutation.mutateAsync(option.value);
                        }}
                      >
                        <span className="truncate-wrap max-w-[150px]">
                          {option.label}
                        </span>
                      </OptionWithDelete>
                    );
                  }}
                />
              )}
            </FieldConnector>
          </div>
          <div className="flex gap-4">
            <FieldConnector
              className="flex-1"
              name="unitAmountExclVAT"
              label={t('invoice.form.price')}
            >
              {({ field: { onChange, value, ...field } }) => {
                return (
                  <PaymentAmount
                    {...field}
                    tracing={{ onFocus: events.WEBAPP_PRICEITEM_CLICKEDPRICE }}
                    value={value == null ? null : divideBy1000(value)}
                    onChange={(value) => {
                      const amount = round(value == null ? 0 : value);
                      onChange(round(multiplyBy1000(amount)));
                    }}
                  />
                );
              }}
            </FieldConnector>
            {!meta.VATRate.isHidden && (
              <FieldConnector
                className="flex-1"
                name="VATRate"
                label={t('invoice.field.vat_rate')}
              >
                {({ field: { onChange, value, ...rest } }) => {
                  return (
                    <Select
                      tracing={{
                        onFocus: events.WEBAPP_PRICEITEM_CLICKEDVATRATE,
                      }}
                      isDisabled={meta.VATRate.isDisabled}
                      options={VATRatesOptions}
                      isSearchable={false}
                      value={value == null ? null : divideBy1000(value)}
                      onChange={(option: any) => {
                        const value = multiplyBy1000(option?.value);
                        onChange(value);
                      }}
                      {...rest}
                    />
                  );
                }}
              </FieldConnector>
            )}
          </div>
          {footerMeta}
        </div>
        <div className="px-6 my-8 text-center">
          <Button
            loading={isSubmitting}
            tracingEvent={events.WEBAPP_PRICEITEM_CLICKEDSUBMIT}
            className="m-auto"
            color="revenues"
            onClick={(e) => {
              formAPI.trigger().then((valid) => {
                if (valid) {
                  return formAPI.handleSubmit(onSubmit)(e);
                }
                const fieldWithError =
                  document.querySelector('.field-with-error');
                if (fieldWithError)
                  fieldWithError.scrollIntoView({ behavior: 'smooth' });
              });
            }}
          >
            {t('save')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default Form;
